import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import useWebSocket from '../hooks/useWebSocket';
import ActivityLogs from '../components/ActivityLogs';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DashboardHeader from './dashboard/DashboardHeader';
import SystemStatsPanel from './dashboard/SystemStatsPanel';
import HospitalFinancialStatus from './dashboard/HospitalFinancialStatus';
import PatientSearchSection from './dashboard/PatientSearchSection';
import ClaimCreationCard from './dashboard/ClaimCreationCard';
import HospitalMonthlyClaimsTable from './dashboard/HospitalMonthlyClaimsTable';
import ClaimsHistoryTable from './dashboard/ClaimsHistoryTable';
import ClaimDetailsModal from './dashboard/ClaimDetailsModal';
import ClaimDraftsModal from './dashboard/ClaimDraftsModal';
import DailySummaryModal from './dashboard/DailySummaryModal';
import MonthlySummaryModal from './dashboard/MonthlySummaryModal';
import useDashboardData from './dashboard/useDashboardData';
import useClaimWorkspace from './dashboard/useClaimWorkspace';

const Dashboard = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const hospitalName = localStorage.getItem('hospital_name');
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');

  const isSuperAdmin = username === 'superadmin' || (role === 'Admin' && hospitalName === 'System Administration');
  const isViewOnly = role === 'ViewOnly';
  const canViewAllData = isSuperAdmin || isViewOnly;

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const [showMonthlySummaryModal, setShowMonthlySummaryModal] = useState(false);
  const [showDailySummaryModal, setShowDailySummaryModal] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const data = useDashboardData({ token, axiosConfig, isSuperAdmin, canViewAllData, hospitalName, username, role });
  const ws = useClaimWorkspace({
    axiosConfig, isSuperAdmin, hospitalName,
    hospitalCurrency: data.hospitalCurrency,
    formatCurrency: data.formatCurrency,
    toLocalBalance: data.getFamilyBalanceInHospitalCurrency,
    loadClaims: data.loadClaims,
    loadHospitalStats: data.loadHospitalStats,
    loadHospitalBalance: data.loadHospitalBalance,
  });

  // Acacia office: how many visit approvals + external referral letters are waiting
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const loadPendingApprovals = () => {
    if (!isSuperAdmin) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/visits/approvals/pending-count`, { withCredentials: true }).then((r) => setPendingApprovals(r.data.count || 0)).catch(() => {});
  };
  useEffect(() => {
    loadPendingApprovals();
    const t = setInterval(loadPendingApprovals, 60000);
    return () => clearInterval(t);
  }, [isSuperAdmin]);

  // WebSocket connection for real-time updates
  useWebSocket((message) => {
    if (message.type === 'visit_updated' || message.type === 'referral_decision' || message.type === 'preauth_decision') loadPendingApprovals();
    if (message.type === 'claim_submitted') {
      data.loadClaims();
      if (isSuperAdmin) {
        data.loadHospitalStats();
      } else {
        data.loadHospitalBalance();
      }
      if (ws.familyInfo) {
        ws.handleSearch();
      }
    } else if (message.type === 'deposit_added') {
      if (isSuperAdmin) {
        data.loadHospitalStats();
      } else if (message.data.hospital_name === hospitalName) {
        data.loadHospitalBalance();
      }
    } else if (message.type === 'family_balance_updated') {
      if (ws.familyInfo && ws.familyInfo.family_id === message.data.family_id) {
        ws.handleSearch();
      }
    } else if (message.type === 'claim_status_changed') {
      data.loadClaims();
    } else if (message.type === 'referral_decision') {
      const d = message.data || {};
      if (isSuperAdmin || d.hospital_name === hospitalName) {
        if (d.decision === 'approved') toast.success(`External referral APPROVED for ${d.patient_name} — ${d.referral_number}. Print the referral letter from Workflow → All Visits.`, { duration: 10000 });
        else toast.error(`External referral REJECTED for ${d.patient_name}${d.reason ? ` — ${d.reason}` : ''}.`, { duration: 10000 });
      }
    } else if (message.type === 'preauth_decision') {
      const d = message.data || {};
      if (isSuperAdmin || d.hospital_name === hospitalName) {
        if (d.decision === 'approved') {
          toast.success(`Pre-authorization APPROVED for ${d.patient_name} — ${d.preauth_number}. The patient can proceed to the doctor.`, { duration: 10000 });
        } else {
          toast.error(`Pre-authorization REJECTED for ${d.patient_name}${d.reason ? ` — ${d.reason}` : ''}. The visit was cancelled.`, { duration: 10000 });
        }
      }
    }
  });

  useEffect(() => {
    if (!token) navigate('/login');
  }, []);

  // Clinical roles work from the Patient Workflow page, not the dashboard
  const isClinicalRole = ['Consultant', 'Gynaecologist', 'Specialist', 'Pharmacist', 'Lab', 'Radiology'].includes(role);
  useEffect(() => {
    if (isClinicalRole) navigate('/workflow', { replace: true });
  }, [isClinicalRole]);

  const handlePrintClaim = (claimId) => {
    window.open(`/print/${claimId}`, '_blank');
  };

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    localStorage.clear();
    navigate('/login');
    toast.info('Logged out');
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f4f8 100%)' }}>
      <DashboardHeader
        role={role}
        hospitalName={hospitalName}
        username={username}
        isSuperAdmin={isSuperAdmin}
        canViewAllData={canViewAllData}
        maintenanceMode={data.maintenanceMode}
        onToggleMaintenance={data.toggleMaintenanceMode}
        workflowEnabled={data.workflowEnabled}
        pendingApprovals={pendingApprovals}
        hospitalStats={data.hospitalStats}
        todayStats={data.todayStats}
        onOpenMonthlySummary={() => setShowMonthlySummaryModal(true)}
        onOpenDailySummary={() => setShowDailySummaryModal(true)}
        onChangePassword={() => setChangePasswordOpen(true)}
        onLogout={handleLogout}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {canViewAllData && <SystemStatsPanel systemStats={data.systemStats} />}

        {role === 'Admin' && !isSuperAdmin && (
          <HospitalFinancialStatus
            totalClaims={data.totalClaims}
            hospitalBalance={data.hospitalBalance}
            hospitalCurrency={data.hospitalCurrency}
            formatCurrency={data.formatCurrency}
          />
        )}

        <PatientSearchSection
          searchQuery={ws.searchQuery}
          setSearchQuery={ws.setSearchQuery}
          onSearch={ws.handleSearch}
          loading={ws.loading}
          selectedPatient={ws.selectedPatient}
          searchType={ws.searchType}
          familyInfo={ws.familyInfo}
          familyMembers={ws.familyMembers}
          familyClaimHistory={ws.familyClaimHistory}
          role={role}
          isSuperAdmin={isSuperAdmin}
          workflowEnabled={data.workflowEnabled}
          preauthRequired={data.preauthRequired}
          hospitalCurrency={data.hospitalCurrency}
          formatCurrency={data.formatCurrency}
          toLocalBalance={data.getFamilyBalanceInHospitalCurrency}
          toLocalAmount={data.getClaimAmountInHospitalCurrency}
          patientBypassStates={ws.patientBypassStates}
          onToggleBypass={ws.togglePatientBypass}
          onCheckIn={ws.handleCheckIn}
          onCreateClaimForMember={ws.handleCreateClaimForMember}
          onViewClaimDetails={ws.handleViewClaimDetails}
        />

        {/* Claim Creation - Superadmin fallback + hospitals without the patient workflow */}
        {ws.selectedPatient && (isSuperAdmin || (!data.workflowEnabled && ['Admin', 'Finance', 'Reception'].includes(role))) && (
          <ClaimCreationCard
            selectedPatient={ws.selectedPatient}
            isSuperAdmin={isSuperAdmin}
            patientBypassStates={ws.patientBypassStates}
            onToggleBypass={ws.togglePatientBypass}
            itemSearchQuery={data.itemSearchQuery}
            setItemSearchQuery={data.setItemSearchQuery}
            filteredPriceList={data.filteredPriceList}
            claimItems={ws.claimItems}
            setClaimItems={ws.setClaimItems}
            onRemoveItem={ws.handleRemoveItem}
            formatCurrency={data.formatCurrency}
            getTotalClaimCost={ws.getTotalClaimCost}
            getTotalClaimCostUSD={ws.getTotalClaimCostUSD}
            toLocalBalance={data.getFamilyBalanceInHospitalCurrency}
            onSubmitClaim={ws.handleSubmitClaim}
            loading={ws.loading}
            claimDrafts={ws.claimDrafts}
            maxDrafts={ws.MAX_DRAFTS}
            onOpenDrafts={() => ws.setShowDraftsModal(true)}
            onSaveDraft={ws.handleSaveDraft}
          />
        )}


        {username === 'superadmin' && <ActivityLogs />}

        {canViewAllData && data.allHospitals.length > 0 && (
          <HospitalMonthlyClaimsTable
            allHospitals={data.allHospitals}
            hospitalStats={data.hospitalStats}
            authCfg={axiosConfig}
          />
        )}

        {(role === 'Finance' || role === 'Admin' || role === 'Reception' || role === 'DataEntry') && (
          <ClaimsHistoryTable
            role={role}
            isSuperAdmin={isSuperAdmin}
            hospitalName={hospitalName}
            claims={data.claims}
            claimsTotal={data.claimsTotal}
            claimsTotalPages={data.claimsTotalPages}
            claimStatusFilter={data.claimStatusFilter}
            onStatusFilterChange={(v) => { data.setClaimStatusFilter(v); data.setClaimPage(1); }}
            claimSearch={data.claimSearch}
            onSearchChange={(v) => { data.setClaimSearch(v); data.setClaimPage(1); }}
            claimPageSize={data.claimPageSize}
            onPageSizeChange={(n) => { data.setClaimPageSize(n); data.setClaimPage(1); }}
            claimPage={data.claimPage}
            setClaimPage={data.setClaimPage}
            hospitalCurrency={data.hospitalCurrency}
            hospitalStats={data.hospitalStats}
            onPrintClaim={handlePrintClaim}
            onMarkAsPaid={ws.handleMarkAsPaid}
            onVoidClaim={ws.handleVoidClaim}
          />
        )}
      </div>

      <MonthlySummaryModal
        open={showMonthlySummaryModal}
        onOpenChange={setShowMonthlySummaryModal}
        canViewAllData={canViewAllData}
        isSuperAdmin={isSuperAdmin}
        hospitalName={hospitalName}
        hospitalStats={data.hospitalStats}
        authCfg={axiosConfig}
      />

      <DailySummaryModal
        open={showDailySummaryModal}
        onOpenChange={setShowDailySummaryModal}
        canViewAllData={canViewAllData}
        isSuperAdmin={isSuperAdmin}
        hospitalName={hospitalName}
        hospitalStats={data.hospitalStats}
        authCfg={axiosConfig}
      />

      <ClaimDetailsModal
        open={ws.showClaimDetailsModal}
        onOpenChange={ws.setShowClaimDetailsModal}
        claim={ws.selectedClaimDetails}
        formatCurrency={data.formatCurrency}
        toLocalAmount={data.getClaimAmountInHospitalCurrency}
      />

      <ClaimDraftsModal
        open={ws.showDraftsModal}
        onOpenChange={ws.setShowDraftsModal}
        claimDrafts={ws.claimDrafts}
        maxDrafts={ws.MAX_DRAFTS}
        editingDraft={ws.editingDraft}
        setEditingDraft={ws.setEditingDraft}
        onLoadDraft={ws.handleLoadDraft}
        onUpdateDraft={ws.handleUpdateDraft}
        onDeleteDraft={ws.handleDeleteDraft}
        formatCurrency={data.formatCurrency}
      />

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ConfirmDialog open={!!ws.confirmDialog} onOpenChange={(o) => !o && ws.setConfirmDialog(null)} {...(ws.confirmDialog || {})} />
    </div>
  );
};

export default Dashboard;
